import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ShoppingCart, Plus, Minus, Star, X } from 'lucide-react'
import { Product } from '../types'
import { api } from '../lib/api'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../components/ProductImage'
import { calculateStoreIsOpen } from '../lib/storeUtils'
import { useImageCache } from '../hooks/useImageCache'
import { getCache, getCacheStale, setCache } from '../lib/cache'
import { useStoreDetail } from '../contexts/StoreDetailContext'

const ProductDetail = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const [searchParams] = useSearchParams()
  const productId = searchParams.get('id')
  const navigate = useNavigate()
  const { addItem } = useCart()
  const { isAuthenticated } = useAuth()
  const { preloadImages } = useImageCache()
  const { getStore, getStoreSync } = useStoreDetail() // Usar StoreDetailContext
  
  const [product, setProduct] = useState<Product | null>(null)
  const [store, setStore] = useState<any>(null)
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(true)

  // Refs para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingProductRef = useRef(false)
  const loadingStoreRef = useRef(false)

  useEffect(() => {
    if (!productId || !storeId) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingProductRef.current || loadingStoreRef.current) return
    
    loadingProductRef.current = true
    loadingStoreRef.current = true

    Promise.all([
      loadProduct().finally(() => { loadingProductRef.current = false }),
      loadStore().finally(() => { loadingStoreRef.current = false })
    ])
  }, [productId, storeId])

  const loadProduct = async () => {
    if (!productId) return
    
    const cacheKey = `product_detail_${productId}`
    
    // Intentar cargar desde cache primero
    const cachedProduct = getCacheStale<Product>(cacheKey)
    if (cachedProduct) {
      setProduct(cachedProduct)
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      setLoading(true)
      const response = await api.get(`/products/${productId}`)
      if (response.data.success && response.data.data) {
        const productData = response.data.data
        // Normalizar variantes para asegurar que image_url esté presente
        const normalizedVariants = (productData.variants || []).map((variant: any) => ({
          id: variant.id,
          variant_name: variant.variant_name,
          variant_type: variant.variant_type,
          stock_quantity: Number(variant.stock_quantity) || 0,
          is_available: variant.is_available !== false,
          sku: variant.sku,
          image_url: variant.image_url || null
        }))

        const normalizedProduct: Product = {
          id: productData.id,
          name: productData.name,
          description: productData.description,
          price: Number(productData.price) || 0,
          imageUrl: productData.image_url || productData.imageUrl,
          categoryId: productData.category_id || '',
          storeId: productData.store_id || storeId || '',
          isAvailable: productData.is_available !== false,
          stockQuantity: Number(productData.stock_quantity) || 0,
          unit: productData.unit || 'unidad',
          isFeatured: productData.is_featured || false,
          variants: normalizedVariants,
          has_variants: normalizedVariants.length > 0
        }
        
        setProduct(normalizedProduct)
        
        // Pre-cargar todas las imágenes de variantes y la imagen principal
        const imageKeys: (string | null)[] = []
        if (normalizedProduct.imageUrl) {
          imageKeys.push(normalizedProduct.imageUrl)
        }
        if (normalizedProduct.variants) {
          normalizedProduct.variants.forEach(variant => {
            if (variant.image_url) {
              imageKeys.push(variant.image_url)
            }
          })
        }
        // Pre-cargar todas las imágenes en paralelo
        if (imageKeys.length > 0) {
          preloadImages(imageKeys).catch(error => {
            console.error('Error preloading images:', error)
          })
        }
        
        // Guardar en cache (30 minutos de TTL)
        setCache(cacheKey, normalizedProduct, 30 * 60 * 1000)
        
        // Si tiene variantes y solo hay una disponible, seleccionarla automáticamente
        if (normalizedProduct.has_variants && normalizedProduct.variants && normalizedProduct.variants.length === 1) {
          const availableVariant = normalizedProduct.variants.find(v => v.is_available && v.stock_quantity > 0)
          if (availableVariant) {
            setSelectedVariant(availableVariant)
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading product:', error)
      toast.error('Error al cargar el producto')
      navigate(`/stores/${storeId}/catalog`)
    } finally {
      setLoading(false)
    }
  }

  const loadStore = async () => {
    if (!storeId) return
    
    // Intentar cargar desde cache sincrónicamente primero (más rápido)
    const cachedStore = getStoreSync(storeId)
    if (cachedStore) {
      setStore(cachedStore)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      // Usar StoreDetailContext para cargar el store (elimina query duplicada)
      const loadedStore = await getStore(storeId)
      if (loadedStore) {
        setStore(loadedStore)
      }
    } catch (error: any) {
      console.error('Error loading store:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStore) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para agregar productos al carrito')
      navigate('/login', { state: { from: `/stores/${storeId}/product?id=${productId}` } })
      return
    }

    if (!store || !product) {
      toast.error('Información del negocio o producto no disponible')
      return
    }

    // Si el producto tiene variantes, debe seleccionarse una
    if (product.has_variants && product.variants && product.variants.length > 0) {
      if (!selectedVariant) {
        toast.error('Por favor selecciona una variante')
        return
      }
      
      if (!selectedVariant.is_available || selectedVariant.stock_quantity === 0) {
        toast.error('Esta variante no está disponible')
        return
      }

      if (quantity > selectedVariant.stock_quantity) {
        toast.error(`Solo hay ${selectedVariant.stock_quantity} unidades disponibles de esta variante`)
        return
      }

      addItem(product, store, quantity, undefined, selectedVariant.id, selectedVariant)
    } else {
      // Producto sin variantes
      if (!product.isAvailable || product.stockQuantity === 0) {
        toast.error('Producto no disponible')
        return
      }

      if (quantity > product.stockQuantity) {
        toast.error(`Solo hay ${product.stockQuantity} unidades disponibles`)
        return
      }

      addItem(product, store, quantity)
    }

    const productName = selectedVariant ? `${product.name} - ${selectedVariant.variant_name}` : product.name
    toast.success(`${quantity} ${productName} agregado${quantity > 1 ? 's' : ''} al carrito`)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando producto...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center px-4">
        <div className="text-center max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
              <X className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Producto no encontrado</h2>
            <p className="text-gray-600 mb-8 text-sm sm:text-base">El producto que buscas no existe o no está disponible</p>
            <button
              onClick={() => navigate(`/stores/${storeId}/catalog`)}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
            >
              Volver al Catálogo
            </button>
          </div>
        </div>
      </div>
    )
  }

  const availableVariants = product.variants?.filter(v => v.is_available && v.stock_quantity > 0) || []
  const currentStock = selectedVariant ? selectedVariant.stock_quantity : product.stockQuantity
  const isAvailable = selectedVariant 
    ? (selectedVariant.is_available && selectedVariant.stock_quantity > 0)
    : (product.isAvailable && product.stockQuantity > 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6 safe-area-bottom">
      {/* iPhone-style Header */}
      <div className="bg-white/95 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-50 shadow-sm safe-area-top">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/stores/${storeId}/catalog`)}
            className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all duration-200 active:scale-95 touch-manipulation"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
          </button>
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 flex-1 text-center px-2 sm:px-4 truncate">
            {product.name}
          </h1>
          <div className="w-9 sm:w-10"></div> {/* Spacer for centering */}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Product Image - Responsive */}
        <div className="relative w-full aspect-square sm:aspect-[4/3] lg:aspect-[16/9] max-h-[60vh] sm:max-h-[70vh] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
          <ProductImage
            imageKey={selectedVariant?.image_url || product.imageUrl || null}
            className="w-full h-full object-cover"
            alt={product.name}
          />
          {product.isFeatured && (
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold shadow-lg flex items-center gap-1.5 sm:gap-2">
              <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current" />
              <span className="hidden sm:inline">Destacado</span>
              <span className="sm:hidden">⭐</span>
            </div>
          )}
        </div>

        {/* Product Info - Responsive Card */}
        <div className="bg-white rounded-t-3xl sm:rounded-3xl -mt-6 sm:mt-6 relative z-10 shadow-lg mx-2 sm:mx-0">
          <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
            {/* Product Name and Price */}
            <div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight">{product.name}</h2>
              {product.description && (
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">{product.description}</p>
              )}
              <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600">
                  RD${(Number(product.price) || 0).toFixed(2)}
                </span>
                <span className="text-xs sm:text-sm text-gray-500">/{product.unit || 'unidad'}</span>
              </div>
            </div>

            {/* Variants Selection - Responsive with Images */}
            {product.has_variants && product.variants && product.variants.length > 0 && (
              <div className="space-y-3 sm:space-y-4 pt-4 sm:pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className="text-base sm:text-lg font-semibold text-gray-900">
                    {product.variants[0]?.variant_type === 'flavor' ? 'Sabor' : 
                     product.variants[0]?.variant_type === 'color' ? 'Color' :
                     product.variants[0]?.variant_type === 'size' ? 'Tamaño' : 'Variante'}
                  </label>
                  {selectedVariant && (
                    <span className="text-xs sm:text-sm text-gray-500">
                      Stock: {selectedVariant.stock_quantity}
                    </span>
                  )}
                </div>
                
                {/* Variants Grid with Images - Responsive */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                  {availableVariants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setSelectedVariant(variant)
                        setQuantity(1)
                      }}
                      className={`relative p-2 sm:p-3 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 active:scale-95 overflow-hidden touch-manipulation ${
                        selectedVariant?.id === variant.id
                          ? 'border-primary-600 bg-primary-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 active:bg-gray-50'
                      }`}
                    >
                      {/* Variant Image */}
                      <div className="w-full aspect-square mb-1.5 sm:mb-2 rounded-lg sm:rounded-xl overflow-hidden bg-gray-100 relative">
                        {variant.image_url && variant.image_url.trim() !== '' ? (
                          <ProductImage
                            imageKey={variant.image_url}
                            className="w-full h-full object-cover"
                            alt={`${variant.variant_name} - ${product.name}`}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <span className="text-xl sm:text-2xl">🍽️</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Variant Name */}
                      <div className="text-center">
                        <p className={`text-xs sm:text-sm font-semibold ${
                          selectedVariant?.id === variant.id ? 'text-primary-700' : 'text-gray-900'
                        }`}>
                          {variant.variant_name}
                        </p>
                        {variant.stock_quantity > 0 && variant.stock_quantity < 10 && (
                          <p className="text-[10px] sm:text-xs text-yellow-600 mt-0.5 sm:mt-1 font-medium">
                            ¡Quedan pocas!
                          </p>
                        )}
                      </div>
                      
                      {/* Selected Indicator */}
                      {selectedVariant?.id === variant.id && (
                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-5 h-5 sm:w-6 sm:h-6 bg-primary-600 rounded-full flex items-center justify-center shadow-md">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                
                {availableVariants.length === 0 && (
                  <p className="text-sm sm:text-base text-red-600 text-center py-3 sm:py-4">No hay variantes disponibles</p>
                )}
              </div>
            )}

            {/* Stock Info - Responsive */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Disponibilidad</span>
                <span className={`text-xs sm:text-sm font-bold ${
                  isAvailable ? 'text-green-600' : 'text-red-600'
                }`}>
                  {isAvailable
                    ? `En Stock (${currentStock})`
                    : 'No Disponible'}
                </span>
              </div>
              {currentStock > 0 && currentStock < 10 && (
                <p className="text-[10px] sm:text-xs text-yellow-600 mt-1.5 sm:mt-2 font-medium">¡Quedan pocas unidades!</p>
              )}
            </div>

            {/* Quantity Selector - Responsive */}
            {isAvailable && (
              <div className="space-y-3 sm:space-y-4">
                <label className="text-base sm:text-lg font-semibold text-gray-900 block">Cantidad</label>
                <div className="flex items-center gap-3 sm:gap-4 bg-gray-50 rounded-xl sm:rounded-2xl p-2 sm:p-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl border-2 border-gray-200 flex items-center justify-center hover:bg-white active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-bold text-lg sm:text-xl text-gray-700 bg-white touch-manipulation"
                    aria-label="Reducir cantidad"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={currentStock}
                    value={quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1
                      setQuantity(Math.min(Math.max(1, val), currentStock))
                    }}
                    className="flex-1 text-center border-0 bg-transparent font-bold text-lg sm:text-xl focus:ring-0 focus:outline-none"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(currentStock, quantity + 1))}
                    disabled={quantity >= currentStock}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl border-2 border-gray-200 flex items-center justify-center hover:bg-white active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-bold text-lg sm:text-xl text-gray-700 bg-white touch-manipulation"
                    aria-label="Aumentar cantidad"
                  >
                    +
                  </button>
                </div>
                <div className="flex items-center justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-base sm:text-lg lg:text-xl font-bold text-primary-600">
                    RD${((Number(product.price) || 0) * quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Add to Cart Button - Responsive */}
            <button
              onClick={handleAddToCart}
              disabled={!isAvailable || (product.has_variants && !selectedVariant)}
              className={`w-full py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-200 shadow-lg active:scale-[0.98] touch-manipulation ${
                isAvailable && (!product.has_variants || selectedVariant)
                  ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">
                {!isAvailable
                  ? 'No Disponible'
                  : product.has_variants && !selectedVariant
                  ? 'Selecciona una Variante'
                  : `Agregar ${quantity} al Carrito - RD$${((Number(product.price) || 0) * quantity).toFixed(2)}`}
              </span>
              <span className="sm:hidden">
                {!isAvailable
                  ? 'No Disponible'
                  : product.has_variants && !selectedVariant
                  ? 'Selecciona Variante'
                  : `Agregar ${quantity} - RD$${((Number(product.price) || 0) * quantity).toFixed(2)}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail

